import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER } from "@/constants/testIds";
import { LocationSearch } from "@/components/LocationSearch";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

// Onboarding step 2 basics: avatar, org name, bio (+AI skill-suggest trigger)
// and location (extracted from Onboarding.jsx in the Phase 15 component
// split — behaviour unchanged).
export function ProfileBasicsSection({
  type, orgName, setOrgName,
  displayName, setDisplayName, realName, setRealName,
  bio, setBio,
  location, lat, onLocationSelect, onLocationText,
  suggesting, hasSuggestions, onSuggestSkills,
}) {
  const { user, setUser } = useAuth();
  return (
    <>
      {type !== "organisation" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Display name</label>
            <Input
              data-testid="onboarding-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex R."
              className="mt-2 h-11"
            />
            <p className="text-xs text-neutral-500 mt-1.5">What everyone sees — leaderboards, stories, collectives.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Real name</label>
            <Input
              data-testid="onboarding-real-name"
              value={realName}
              onChange={(e) => setRealName(e.target.value)}
              placeholder="Your full name"
              className="mt-2 h-11"
            />
            <p className="text-xs text-neutral-500 mt-1.5">Kept private unless you choose to show it (Privacy below).</p>
          </div>
        </div>
      )}
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Profile picture</label>
        <div className="flex items-center gap-4 mt-3">
          {user?.picture && <img src={user.picture.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture} alt="avatar" className="w-16 h-16 rounded-full border border-neutral-200 object-cover" />}
          <input
            data-testid="onboarding-avatar"
            type="file" accept="image/*"
            onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              try {
                const res = await api.uploadImage(f);
                await api.setAvatar(res.url);
                setUser({ ...user, picture: res.url });
                toast.success("Avatar updated");
              } catch { toast.error("Upload failed"); }
            }}
            className="text-sm"
          />
        </div>
      </div>
      {type === "organisation" && (
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Organisation name</label>
          <Input
            data-testid={VOLUNTEER.onboardingOrgName}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="GreenFields Trust"
            className="mt-2 h-11"
          />
        </div>
      )}
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Short bio</label>
        <Textarea
          data-testid={VOLUNTEER.onboardingBio}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder={type === "organisation" ? "Tell volunteers about your mission." : "Why do you want to volunteer?"}
          rows={3}
          className="mt-2"
        />
        {type !== "organisation" && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              data-testid="suggest-skills-btn"
              variant="outline"
              size="sm"
              onClick={onSuggestSkills}
              disabled={suggesting || !bio.trim()}
              className="rounded-full h-8 text-xs border-forest/30 text-forest hover:bg-forest/5"
            >
              {suggesting ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reading your bio…</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Suggest skills from your bio</>
              )}
            </Button>
            {hasSuggestions && (
              <span className="text-xs text-neutral-500">Tap a suggestion below to add it.</span>
            )}
          </div>
        )}
      </div>
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Location</label>
        <div className="mt-2">
          <LocationSearch
            testId={VOLUNTEER.onboardingLocation}
            value={location}
            placeholder="Town, city or UK postcode (e.g. London or SW1A 1AA)"
            onSelect={onLocationSelect}
            onTextChange={onLocationText}
          />
        </div>
        <p className="text-xs text-neutral-500 mt-1.5">
          {lat != null ? "✓ Location pinned — this powers local alerts and distance matching." : "Pick a suggestion to enable local alerts and nearby matches."}
        </p>
      </div>
    </>
  );
}
